import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MapPin, Send, CheckCircle2, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const services = [
    "Driveways",
    "Patios",
    "Garden Design",
    "Grounds Maintenance",
    "Tree Surgery",
    "Hedge Cutting",
    "Emergency Service",
    "Other",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email || !formData.service) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/enquiries`, formData);
      setIsSubmitted(true);
      toast.success("Thank you! We'll be in touch soon.");
      setFormData({
        name: "",
        phone: "",
        email: "",
        service: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 lg:py-32 bg-[#F9F9F7]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="text-sm font-medium tracking-wide uppercase text-[#8B5A2B] mb-4 block">
            Get In Touch
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A4D2E] mb-6">
            Request a Free Quote
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Fill out the form below and we'll get back to you within 24 hours with a free, no-obligation quote.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-[#1A4D2E] rounded-2xl p-8 text-white mb-8">
              <h3 className="font-heading text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <a 
                  href="tel:07968790128" 
                  className="flex items-start gap-4 hover:opacity-80 transition-opacity"
                  data-testid="contact-phone-link"
                >
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#D97706]" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm mb-1">Phone</p>
                    <p className="text-xl font-bold font-heading">07968 790128</p>
                  </div>
                </a>

                <a 
                  href="https://www.facebook.com/pioneertreesandlandscapes" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 hover:opacity-80 transition-opacity"
                  data-testid="contact-facebook-link"
                >
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <Facebook className="w-6 h-6 text-[#D97706]" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm mb-1">Facebook</p>
                    <p className="font-semibold">Pioneer Trees and Landscapes</p>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#D97706]" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm mb-1">Address</p>
                    <p className="font-semibold">
                      15 Second Row<br />
                      Ellington<br />
                      NE61 5HE
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <Button 
              asChild
              size="lg"
              className="w-full bg-[#1A4D2E] hover:bg-[#143D24] text-white font-bold py-6 rounded-full text-lg"
              data-testid="contact-call-btn"
            >
              <a href="tel:07968790128" className="flex items-center justify-center gap-2">
                <Phone className="w-5 h-5" />
                Call Now: 07968 790128
              </a>
            </Button>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {isSubmitted ? (
              <div className="bg-white rounded-2xl p-8 shadow-lg text-center" data-testid="form-success">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-[#1A4D2E] mb-4">
                  Thank You!
                </h3>
                <p className="text-gray-600 mb-6">
                  Your enquiry has been submitted successfully. We'll be in touch within 24 hours.
                </p>
                <Button
                  onClick={() => setIsSubmitted(false)}
                  variant="outline"
                  className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                  data-testid="submit-another-btn"
                >
                  Submit Another Enquiry
                </Button>
              </div>
            ) : (
              <form 
                onSubmit={handleSubmit} 
                className="bg-white rounded-2xl p-8 shadow-lg"
                data-testid="contact-form"
              >
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-700 font-medium mb-2 block">
                        Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className="h-12 bg-white border-gray-200 focus:border-[#1A4D2E] focus:ring-[#1A4D2E]/20"
                        required
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-gray-700 font-medium mb-2 block">
                        Phone *
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Your phone number"
                        className="h-12 bg-white border-gray-200 focus:border-[#1A4D2E] focus:ring-[#1A4D2E]/20"
                        required
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="h-12 bg-white border-gray-200 focus:border-[#1A4D2E] focus:ring-[#1A4D2E]/20"
                      required
                      data-testid="input-email"
                    />
                  </div>

                  <div>
                    <Label htmlFor="service" className="text-gray-700 font-medium mb-2 block">
                      Service Required *
                    </Label>
                    <Select onValueChange={handleServiceChange} value={formData.service}>
                      <SelectTrigger 
                        className="h-12 bg-white border-gray-200 focus:border-[#1A4D2E] focus:ring-[#1A4D2E]/20"
                        data-testid="select-service"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent data-testid="select-service-content">
                        {services.map((service, index) => (
                          <SelectItem 
                            key={service} 
                            value={service}
                            data-testid={`select-service-item-${index}`}
                          >
                            {service}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-700 font-medium mb-2 block">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project..."
                      rows={4}
                      className="bg-white border-gray-200 focus:border-[#1A4D2E] focus:ring-[#1A4D2E]/20 resize-none"
                      data-testid="input-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#1A4D2E] hover:bg-[#143D24] text-white font-bold py-6 rounded-full text-lg disabled:opacity-50"
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Send Enquiry
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
